import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import appLoaderAction from '../../redux/action/app-loader-action';
import notifyUtitls from '../../utils/notify-utils';

/**
 * NMU-524: one upload-status standard for every upload in the admin.
 *
 * Before this, choosing a file did nothing visible until the picture appeared —
 * no spinner, no progress, no message, and nothing at all when it failed. Both
 * upload components now run their antd `onChange` through this hook, so an image
 * upload in the Splash Page dialog and a document upload in the news editor
 * behave identically rather than each page inventing its own.
 *
 * What it gives, matching the ticket point for point:
 *   - the global overlay while an upload is in flight, labelled "กำลังอัปโหลด
 *     ไฟล์... n%" — which also blocks a second file choice, a second save and a
 *     duplicate request, since the overlay covers the page;
 *   - "อัปโหลดไฟล์สำเร็จ" with the file's name when it lands;
 *   - the overlay cleared and a reason shown when it does not;
 *   - nothing to undo afterwards: the trigger is live again, so "try another
 *     file" is simply choosing another file.
 */

/** NMU-520: 413 is what nginx answers above `client_max_body_size`. */
const failureMessage = (file, sizeHint) => {
    const status = file?.error?.status;
    if (status === 413) {
        return sizeHint
            ? `อัปโหลดไม่สำเร็จ ไฟล์มีขนาดใหญ่เกินกว่าที่ระบบรองรับ กรุณาเลือกไฟล์ที่มีขนาดไม่เกิน ${sizeHint}`
            : 'อัปโหลดไม่สำเร็จ ไฟล์มีขนาดใหญ่เกินกว่าที่ระบบรองรับ';
    }
    if (status === 401 || status === 403) {
        return 'อัปโหลดไม่สำเร็จ เซสชันหมดอายุ กรุณาเข้าสู่ระบบใหม่อีกครั้ง';
    }
    if (status === 415) {
        return 'อัปโหลดไม่สำเร็จ ระบบไม่รองรับไฟล์ประเภทนี้';
    }
    return 'อัปโหลดไม่สำเร็จ ระบบขัดข้อง กรุณาลองใหม่อีกครั้ง';
};

/**
 * @param sizeHint human-readable size limit for the 413 message, e.g. "5 MB".
 * @returns an antd Upload `onChange` handler. Pass the caller's own handler in
 *   and it still runs, unchanged, after the status has been reported.
 */
export default function useUploadStatus(sizeHint) {
    const dispatch = useDispatch();
    /**
     * Uploads still in flight, by antd's per-file uid.
     *
     * Counted rather than a boolean because the document gallery uploads several
     * files at once: the first one to finish would otherwise pull the overlay
     * away while the rest were still going.
     */
    const inFlight = useRef(new Set());

    return useCallback(
        (onChange) => (info) => {
            const file = info?.file;
            const uid = file?.uid;
            const status = file?.status;

            if (status === 'uploading') {
                if (uid) inFlight.current.add(uid);
                const percent = Math.round(file.percent || 0);
                dispatch(
                    appLoaderAction.enable(
                        percent > 0 && percent < 100
                            ? `กำลังอัปโหลดไฟล์... ${percent}%`
                            : 'กำลังอัปโหลดไฟล์...',
                    ),
                );
            } else if (status === 'done' || status === 'error') {
                if (uid) inFlight.current.delete(uid);
                if (inFlight.current.size === 0) dispatch(appLoaderAction.disale());

                if (status === 'done') {
                    notifyUtitls.success(
                        'สำเร็จ',
                        file.name ? `อัปโหลดไฟล์สำเร็จ: ${file.name}` : 'อัปโหลดไฟล์สำเร็จ',
                    );
                } else {
                    notifyUtitls.error({ title: 'แจ้งเตือน', message: failureMessage(file, sizeHint) });
                }
            }

            if (onChange) onChange(info);
        },
        [dispatch, sizeHint],
    );
}

export { failureMessage };
